import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";

interface TakeReadingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: any;
}

export function TakeReadingDialog({ open, onOpenChange, profile }: TakeReadingDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [readings, setReadings] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alerts, setAlerts] = useState<string[]>([]);

  const parameters = (profile.parameters as any[]) || [];

  const handleValueChange = (paramName: string, value: string, min: number, max: number) => {
    const numValue = parseFloat(value);
    setReadings({ ...readings, [paramName]: numValue });

    // Check for out-of-range values
    if (numValue < min || numValue > max) {
      setAlerts([...alerts.filter(a => !a.includes(paramName)), 
        `${paramName} is out of range (${min}-${max})`]);
    } else {
      setAlerts(alerts.filter(a => !a.includes(paramName)));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (!profileData) throw new Error("Profile not found");

      const alertsTriggered = alerts.map(alert => ({
        message: alert,
        timestamp: new Date().toISOString(),
      }));

      // Direct insert since RLS is disabled
      const { error: insertError } = await supabase
        .from('special_monitoring_readings')
        .insert({
          profile_id: profile.id,
          readings: parameters.map(p => ({
            name: p.name,
            value: readings[p.name] || 0,
            unit: p.unit,
          })),
          recorded_by: profileData.id,
          notes: notes || "",
          alerts_triggered: alertsTriggered,
        });

      if (insertError) throw insertError;

      if (alerts.length > 0) {
        toast({
          title: "Alert",
          description: `${alerts.length} parameter(s) out of range`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Reading recorded successfully",
        });
      }

      queryClient.invalidateQueries({ queryKey: ['monitoringReadings', profile.id] });
      onOpenChange(false);
      setReadings({});
      setNotes("");
      setAlerts([]);
    } catch (error) {
      console.error('Error recording reading:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to record reading",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Take Reading</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {alerts.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="space-y-1">
                  <div className="font-semibold text-destructive">Alerts</div>
                  {alerts.map((alert, index) => (
                    <div key={index} className="text-sm text-destructive">{alert}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-4">
            {parameters.map((param: any) => (
              <div key={param.name}>
                <Label>{param.name} ({param.unit})</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    placeholder={`${param.min} - ${param.max}`}
                    value={readings[param.name] || ""}
                    onChange={(e) =>
                      handleValueChange(param.name, e.target.value, param.min, param.max)
                    }
                    required
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {param.min}-{param.max}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              placeholder="Add any observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Record Reading
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
